import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check } from 'lucide-react';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { useTodayPlan, type GoalSummary, type PlanReason, type PlanTest, type TodayPlan } from 'modules/studyPlan';
import { catalogLink, examKey, testCost } from 'modules/studyPlan/labels';
import { categorySurface, categoryTheme } from './categoryThemes';

const REASON_KEYS: Record<PlanReason, string> = {
  DIAGNOSTIC: 'studyPlan.reason_diagnostic',
  NEXT_NEW: 'studyPlan.reason_next_new',
  RETAKE: 'studyPlan.reason_retake',
  RETAKE_LOW: 'studyPlan.reason_retake_low',
  RESUME: 'studyPlan.reason_resume',
  RELATED: 'studyPlan.reason_related',
};

const EYEBROW_KEYS = {
  DIAGNOSTIC: 'studyPlan.eyebrow_diagnostic',
  RESUME: 'studyPlan.eyebrow_resume',
  READY: 'studyPlan.eyebrow_today',
} as const;

// The test card sits on a tint of its exam's colour (see categoryThemes), so its text is plain foreground.
const inkMuted = 'text-foreground/70';
const inkFaint = 'text-foreground/60';
const chipClass =
  'inline-flex h-[26px] items-center rounded-md border border-foreground/15 bg-card/60 px-2.5 text-[12.5px] font-medium text-foreground/90';

/** The study plan's exam ids as the category the test belongs to, for its colour. */
export function examThemeKey(exam: string): string {
  const key = exam.toLowerCase().replace(/_/g, '-');
  return key === 'sat-ielts' ? 'ielts' : key;
}

/**
 * The learner's one test for today, chosen by the study plan from the exam they
 * picked at onboarding. Shows nothing for accounts without a learner profile.
 */
export function TodayTestCard() {
  const { t } = useTranslation();
  const { data: plan, isLoading, isError, refetch } = useTodayPlan();

  if (isLoading) return <Skeleton className="h-[236px] rounded-xl" />;

  if (isError) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-5 py-4">
        <span className="inline-flex items-center gap-2 text-[13px] text-error-700 dark:text-error-400">
          <AlertCircle size={16} aria-hidden="true" />
          {t('failed_to_load')}
        </span>
        <Button variant="outline" size="xs" onClick={() => refetch()}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  if (!plan) return null;
  return (
    <>
      <TodayTestBody plan={plan} />
      <GoalGapNotice goals={plan.goals ?? []} />
    </>
  );
}

/**
 * Asks the learner to finish a goal that the plan cannot read exactly: one saved by an older form (a typed level,
 * the old combined SAT / IELTS, an attestation goal with no subject). It opens the goal form at that exam.
 */
function GoalGapNotice({ goals }: { goals: GoalSummary[] }) {
  const { t } = useTranslation();
  const goal = goals.find((g) => g.gaps.length > 0);
  if (!goal) return null;
  const exam = goal.knownExam ? t(examKey(goal.exam), { defaultValue: goal.exam }) : t('studyPlan.gap_unknown_exam');
  const to = `/portal/settings/plan?goal=edit${goal.knownExam ? `&exam=${encodeURIComponent(goal.exam)}` : ''}`;
  return (
    <section aria-labelledby="goal-gap-title" className="flex flex-col gap-3 rounded-lg border border-border bg-card px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <h2 id="goal-gap-title" className="text-sm font-semibold text-foreground">{t('studyPlan.gap_title', { exam })}</h2>
        <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{t(`studyPlan.gap_${goal.gaps[0]}`)}</p>
      </div>
      <Button size="sm" className="shrink-0" asChild>
        <Link to={to}>{t('studyPlan.gap_action')}</Link>
      </Button>
    </section>
  );
}

function TodayTestBody({ plan }: { plan: TodayPlan }) {
  const { t } = useTranslation();
  const { goal } = plan;
  // An older server sends neither list; the card then shows the one test it always did.
  const planned = plan.tests ?? [];
  const doneToday = plan.doneToday ?? [];
  const finishedToday = doneToday.length > 0 ? doneToday : plan.completedToday ? [plan.completedToday] : [];
  const isAhead = plan.status === 'DONE' && Boolean(plan.tomorrow);
  const test = plan.test ?? (isAhead ? plan.tomorrow : null);
  const several = (plan.goals?.length ?? 0) > 1;
  const examLabel = (id: string) => t(examKey(id), { defaultValue: id });
  const exam = examLabel(goal.targetExam);
  // A test is tagged with the exam it was written for when that is a related one, and with its goal when there are several.
  const examOf = (other: PlanTest) =>
    other.relatedExam ? examLabel(other.relatedExam) : several && other.goalExam ? examLabel(other.goalExam) : null;
  const catalog = catalogLink(goal, t);
  const seeAll = goal.knownExam ? (
    <Link
      to={catalog.to}
      className="rounded-md px-1 py-2 text-[13.5px] font-medium underline underline-offset-4 max-md:inline-flex max-md:min-h-11 max-md:items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {catalog.label}
    </Link>
  ) : null;

  if (plan.status === 'NO_TEST' || !test) {
    if (plan.status === 'DONE') {
      return (
        <section aria-labelledby="today-test-title" className="rounded-lg border border-border bg-card p-5 md:p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
              <Check size={15} strokeWidth={3} aria-hidden="true" />
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <h2 id="today-test-title" className="text-[15px] font-semibold text-foreground">
                {t('studyPlan.eyebrow_done')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('studyPlan.all_completed_body')}</p>
            </div>
          </div>
          {finishedToday.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5 pl-10">
              {finishedToday.map((item) => (
                <li key={item.attemptId} className="flex items-center justify-between gap-3">
                  {item.title && <span className="min-w-0 break-words text-sm text-foreground/80">{item.title}</span>}
                  <Link
                    to={`/portal/results/${item.attemptId}`}
                    className={`shrink-0 rounded-md text-[13.5px] font-medium text-foreground underline underline-offset-4 max-md:inline-flex max-md:min-h-11 max-md:items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      item.title ? '' : 'ml-auto'
                    }`}
                  >
                    {t('studyPlan.see_report')}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      );
    }

    if (!goal.knownExam) return null;
    return (
      <section aria-labelledby="today-test-title" className="rounded-lg border border-border bg-card p-5 md:p-6">
        <h2 id="today-test-title" className="text-[15px] font-semibold text-foreground">
          {t('studyPlan.none_title', { exam })}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('studyPlan.none_body')}</p>
        <Button variant="outline" size="sm" className="mt-4" asChild>
          <Link to={catalog.to}>{catalog.label}</Link>
        </Button>
      </section>
    );
  }

  const others = planned.filter((other) => other.id !== test.id);
  const eyebrow = isAhead
    ? null
    : EYEBROW_KEYS[plan.status === 'DIAGNOSTIC' ? 'DIAGNOSTIC' : plan.status === 'RESUME' ? 'RESUME' : 'READY'];
  const source = examOf(test);
  // The reason speaks of the exam the test is for, which with several goals need not be the nearest.
  const testExam = test.goalExam ? examLabel(test.goalExam) : exam;
  const reason = plan.reason ? t(REASON_KEYS[plan.reason], test.relatedExam && source ? { exam: testExam, source } : { exam: testExam }) : null;
  const daysLeft = goal.daysLeft !== null && goal.daysLeft >= 0 ? goal.daysLeft : null;
  const { accent, icon: ThemeIcon } = categoryTheme(examThemeKey(test.goalExam ?? goal.targetExam));
  const surface = categorySurface(accent);

  return (
    <section
      aria-labelledby="today-test-title"
      style={{ background: surface.background, borderColor: surface.borderColor }}
      className="relative overflow-hidden rounded-[22px] border p-5 md:p-7"
    >
      <ThemeIcon
        aria-hidden="true"
        strokeWidth={1.2}
        className="pointer-events-none absolute -right-5 -top-8 h-[190px] w-[190px] rotate-[-8deg] opacity-[0.14] md:h-[240px] md:w-[240px]"
        style={{ color: accent }}
      />
      <div className="relative flex items-center justify-between gap-3">
        {isAhead ? (
          <div className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white dark:bg-emerald-400 dark:text-neutral-950">
              <Check size={10} strokeWidth={3} aria-hidden="true" />
            </span>
            <p className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${inkFaint}`}>
              {t('studyPlan.eyebrow_done_ahead')}
            </p>
          </div>
        ) : (
          <p
            className="inline-flex rounded-full bg-card/70 px-3 py-1 text-[11.5px] font-extrabold tracking-[0.02em]"
            style={{ color: surface.color }}
          >
            {t(eyebrow!)}
          </p>
        )}
        {daysLeft !== null && <p className={`text-xs ${inkMuted}`}>{t('studyPlan.days_left', { count: daysLeft })}</p>}
      </div>
      <h2
        id="today-test-title"
        className="relative mt-3 font-display text-2xl font-extrabold leading-tight tracking-[-0.03em] md:text-[28px]"
      >
        {test.title}
      </h2>
      <ul className="relative mt-3 flex flex-wrap gap-1.5">
        {test.questionsCount > 0 && (
          <li className={chipClass}>
            {test.questionsCount} {t('portal.questions')}
          </li>
        )}
        {test.durationMinutes > 0 && (
          <li className={chipClass}>
            {test.durationMinutes} {t('portal.minutes')}
          </li>
        )}
        <li className={chipClass}>{testCost(test, t)}</li>
        {source && <li className={chipClass}>{source}</li>}
      </ul>
      {reason && <p className={`relative mt-3 max-w-xl text-[14.5px] leading-relaxed ${inkMuted}`}>{reason}</p>}
      <div className="relative mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
        <Button size="lg" className="w-full bg-foreground text-background hover:bg-foreground/90 sm:w-auto" asChild>
          <Link to={`/portal/tests/take/${test.id}`}>{plan.status === 'RESUME' ? t('studyPlan.resume') : t('studyPlan.start')} →</Link>
        </Button>
        <Button size="lg" variant="outline" className="w-full border-foreground/15 bg-card sm:w-auto" asChild>
          <Link to="/portal/settings/history">{t('portal.history')}</Link>
        </Button>
        {seeAll}
      </div>
      {others.length > 0 && (
        <div className="relative mt-6 border-t border-foreground/15 pt-4">
          <p id="also-today-title" className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${inkFaint}`}>{t('studyPlan.also_today')}</p>
          <ul data-testid="also-today" aria-labelledby="also-today-title" className="mt-2 flex flex-col">
            {others.map((other) => (
              <li key={other.id} className="flex items-center justify-between gap-3 py-1.5">
                <Link
                  to={`/portal/tests/take/${other.id}`}
                  className="min-w-0 break-words rounded-md text-[14.5px] font-medium underline-offset-4 hover:underline max-md:min-h-11 max-md:py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {other.title}
                </Link>
                <span className={`shrink-0 text-xs ${inkMuted}`}>
                  {[
                    other.questionsCount > 0 ? `${other.questionsCount} ${t('portal.questions')}` : null,
                    other.durationMinutes > 0 ? `${other.durationMinutes} ${t('portal.minutes')}` : null,
                    testCost(other, t),
                    examOf(other),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {finishedToday.length > 0 && (
        <div className="relative mt-5 border-t border-foreground/15 pt-3.5">
          <p className={`text-[12px] font-medium ${inkFaint}`}>
            {t('studyPlan.done_so_far', { count: finishedToday.length })}
          </p>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {finishedToday.map((item) => (
              <li key={item.attemptId} className="flex items-center justify-between gap-3">
                <Link
                  to={`/portal/results/${item.attemptId}`}
                  className="min-w-0 break-words text-[13.5px] font-medium underline underline-offset-4 hover:underline max-md:inline-flex max-md:min-h-11 max-md:items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {item.title ?? t('studyPlan.see_report')}
                </Link>
                {item.title && (
                  <Link
                    to={`/portal/results/${item.attemptId}`}
                    className="shrink-0 rounded-md text-[13px] font-medium underline underline-offset-4 max-md:inline-flex max-md:min-h-11 max-md:items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t('studyPlan.see_report')}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default TodayTestCard;
